import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { HiMenu, HiX } from 'react-icons/hi';
import { SCHEDULE_PATH, TRANSPORT_PATH, juniorCupPath } from '../config/externalLinks';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import './Header.css';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMenuOpen, closeMenu]);

  const scrollToSection = (sectionId: string) => {
    closeMenu();

    const currentHash = window.location.hash.replace('#', '');
    const onSubPage = window.location.pathname.replace(/\/+$/, '') !== '';
    if (
      onSubPage ||
      currentHash === 'junior-cup' ||
      currentHash === 'laliga-junior' ||
      currentHash === 'cup-scores' ||
      currentHash === 'cup-data'
    ) {
      if (sectionId === 'home') {
        window.location.href = window.location.origin + '/';
      } else {
        window.location.href = window.location.origin + '/#' + sectionId;
      }
    } else {
      if (sectionId === 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const toggleMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen((open) => !open);
  };

  return (
    <motion.header
      className={`header header--hamburger-nav ${isScrolled ? 'header--scrolled' : ''}`}
      style={{ overflow: 'visible' }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="header__content">
        <div
          className="header__logo"
          onClick={() => {
            window.location.href = window.location.origin + '/';
          }}
          style={{ cursor: 'pointer' }}
        >
          <img
            src="/newlogo.png"
            alt="FK Junior Petrovaradin"
            className="header__logo-img"
            decoding="async"
          />
        </div>

        <div className="header__actions">
          <nav className="header__quick" aria-label="Quick links">
            <a href="/vesti/">{t('header.news')}</a>
            <a href={juniorCupPath(language)}>{t('header.juniorCup2027')}</a>
            <a className="header__quick-cta" href="/upis/">{t('header.enroll')}</a>
          </nav>
          <div className="header__bar-lang">
            <LanguageSwitcher />
          </div>
          <button
            type="button"
            className="header__menu-toggle"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-controls="header-menu-panel"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <HiX /> : <HiMenu />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <>
          <div
            className="header__backdrop"
            aria-hidden
            onClick={closeMenu}
          />
          <nav
            id="header-menu-panel"
            className="header__panel"
            aria-label="Main navigation"
          >
            <ul className="header__panel-nav">
              <li>
                <button type="button" onClick={() => scrollToSection('home')}>
                  {t('header.home')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('skola-fudbala')}>
                  {t('header.school')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('valencia-partnership')}>
                  {t('header.valenciaPartnership')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('training-experience')}>
                  {t('header.trainingExperience')}
                </button>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('achievements')}>
                  {t('header.achievements')}
                </button>
              </li>
              <li>
                <a className="header__panel-external header__panel-nav--emphasis" href="/upis/" onClick={closeMenu}>
                  {t('header.enroll')}
                </a>
              </li>
              <li>
                <a className="header__panel-external" href={juniorCupPath(language)} onClick={closeMenu}>
                  {t('header.juniorCup2027')}
                </a>
              </li>
              <li>
                <a className="header__panel-external" href="/vesti/" onClick={closeMenu}>
                  {t('header.news')}
                </a>
              </li>
              <li>
                <a className="header__panel-external" href={SCHEDULE_PATH} onClick={closeMenu}>
                  {t('header.schedule')}
                </a>
              </li>
              <li>
                <a className="header__panel-external" href={TRANSPORT_PATH} onClick={closeMenu}>
                  {t('header.transport')}
                </a>
              </li>
              <li>
                <button type="button" onClick={() => scrollToSection('contact')}>
                  {t('header.contact')}
                </button>
              </li>
            </ul>
          </nav>
        </>
      )}
    </motion.header>
  );
};

export default Header;
